'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import LoginForm from './LoginForm';
import SocialLogin from './SocialLogin';
import VoiceLogin from './VoiceLogin';
import GuestBrowseLink from './GuestBrowseLink';

interface MockCredentials {
  email: string;
  password: string;
  role: 'consumer' | 'admin';
}

const LoginInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isVoiceActive, setIsVoiceActive] = useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();

  const mockCredentials: MockCredentials[] = [
    { email: 'consumer@fishbite.com', password: 'consumer123', role: 'consumer' },
    { email: '9876543210', password: 'consumer123', role: 'consumer' },
    { email: 'admin@fishbite.com', password: 'admin123', role: 'admin' },
    { email: '9123456789', password: 'admin123', role: 'admin' },
  ];

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    setError(null);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));

    const user = mockCredentials.find(
      cred => cred.email === email && cred.password === password
    );

    if (user) {
      // Store user data in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('userRole', user.role);
        localStorage.setItem('userEmail', email);
      }

      // Redirect based on role
      const redirectTo = searchParams?.get('redirect') || 
        (user.role === 'admin' ? '/admin-dashboard' : '/home-dashboard');
      router.push(redirectTo);
    } else {
      setError('Invalid credentials. Use: consumer@fishbite.com / consumer123 or admin@fishbite.com / admin123');
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: 'google' | 'facebook') => {
    setIsLoading(true);
    setError(null);

    // Simulate social login
    await new Promise(resolve => setTimeout(resolve, 2000));

    if (typeof window !== 'undefined') {
      localStorage.setItem('isAuthenticated', 'true');
      localStorage.setItem('userRole', 'consumer');
      localStorage.setItem('userEmail', `user@${provider}.com`);
    }

    router.push('/home-dashboard');
  };

  const handleVoiceActivate = () => {
    setIsVoiceActive(!isVoiceActive);
    if (!isVoiceActive) {
      setError(null);
    }
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="animate-pulse space-y-6">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="h-64 bg-muted rounded-lg"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 bg-primary rounded-xl shadow-elevation-md">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M24 6C24 6 12 12 12 24C12 30 15 36 24 42C33 36 36 30 36 24C36 12 24 6 24 6Z" fill="white" opacity="0.9"/>
                <ellipse cx="24" cy="21" rx="4.5" ry="3" fill="white"/>
                <path d="M19.5 27C19.5 27 21 30 24 30C27 30 28.5 27 28.5 27" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
          </div>
          <h1 className="font-heading font-bold text-3xl text-foreground mb-2">
            Welcome to FishBite
          </h1>
          <p className="text-sm text-muted-foreground">
            Login to access fresh seafood delivered to your doorstep
          </p>
        </div>

        {/* Login Form */}
        <LoginForm 
          onSubmit={handleLogin}
          isLoading={isLoading}
          error={error}
        />

        {/* Social Login */}
        <SocialLogin 
          onSocialLogin={handleSocialLogin}
          isLoading={isLoading}
        />

        {/* Voice Login */}
        <VoiceLogin 
          onVoiceActivate={handleVoiceActivate}
          isActive={isVoiceActive}
        />

        {/* Guest Browse Link */}
        <GuestBrowseLink />

        {/* Footer Info */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            By continuing, you agree to FishBite's{' '}
            <a href="#" className="text-primary hover:underline">Terms of Service</a>
            {' '}and{' '}
            <a href="#" className="text-primary hover:underline">Privacy Policy</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginInteractive;